"use client";

// Mirrors iOS FaceOff/Shared/Utilities/AttachmentTextStats.swift. Computes
// lightweight content stats for an attachment so the auto-mode classifier can
// route on actual content volume (chars / pages) — not byte size, which is a
// poor proxy. Image-heavy PDFs are huge but low-info; dense academic PDFs
// are tiny but high-info.

export interface AttachmentStats {
  /** Char count of sampled text. PDFs are capped at 20 pages. nil when the
   *  type can't be cheaply previewed (images, DOCX, etc.). */
  textChars?: number;
  /** Total pages in a PDF (full count, not capped). nil for non-PDF types. */
  pageCount?: number;
  /** How many pages were actually scanned for the char count. nil for
   *  non-PDF types. Lets the classifier pro-rate density. */
  sampledPages?: number;
}

const PDF_PAGE_CAP = 20;

let pdfjsModulePromise: Promise<typeof import("pdfjs-dist")> | null = null;

async function loadPdfjs(): Promise<typeof import("pdfjs-dist")> {
  if (pdfjsModulePromise) return pdfjsModulePromise;
  pdfjsModulePromise = (async () => {
    const lib = await import("pdfjs-dist");
    // Worker file copied into web/public/ at build time. /pdf.worker.min.mjs
    // works for both `next dev` (served from /public) and `next build`
    // static export (asset baked into out/).
    if (typeof window !== "undefined") {
      lib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
    }
    return lib;
  })();
  return pdfjsModulePromise;
}

/**
 * Returns content stats for a File — fast (under ~150ms even on multi-MB
 * PDFs) and fail-soft. Any extraction error returns all-undefined stats.
 */
export async function computeAttachmentStats(file: File): Promise<AttachmentStats> {
  const mime = (file.type || "").toLowerCase();
  if (mime === "application/pdf") {
    return computePdfStats(file);
  }
  if (mime.startsWith("text/") || mime.includes("markdown") || mime.includes("csv")) {
    try {
      const text = await file.text();
      return { textChars: text.length };
    } catch {
      return {};
    }
  }
  // DOCX, XLSX, PPTX, images — no cheap local extraction.
  return {};
}

async function computePdfStats(file: File): Promise<AttachmentStats> {
  try {
    const pdfjs = await loadPdfjs();
    const buf = await file.arrayBuffer();
    const doc = await pdfjs.getDocument({ data: buf }).promise;
    const totalPages = doc.numPages;
    const sampled = Math.min(totalPages, PDF_PAGE_CAP);
    let totalChars = 0;
    for (let i = 1; i <= sampled; i++) {
      try {
        const page = await doc.getPage(i);
        const content = await page.getTextContent();
        for (const item of content.items) {
          if ("str" in item && typeof item.str === "string") {
            totalChars += item.str.length;
          }
        }
      } catch {
        // Skip page on error; keep going.
      }
    }
    return {
      textChars: totalChars,
      pageCount: totalPages,
      sampledPages: sampled,
    };
  } catch {
    return {};
  }
}
